import type { AssertLooselyAssignable, Url } from "@solomon-labs/types";
import type { Environment } from "@solomon-labs/types-enums/environment";

import rawConfig from "./config.json" with { type: "json" };
import { resolveAppEnvironment } from "./environment";
import { networkConnection } from "./network";
export { SOLOMON_DEVNET, USDV_MINT_ADDRESS } from "./network";

// Replaced by the main app's esbuild plugin; absent in other app builds/tests.
declare const __FEATURE_ADVANCED_ROUTING__: boolean | undefined;
declare const __FEATURE_DISCOVERY__: boolean | undefined;
declare const __FEATURE_RETAIL_LP_API_DISCOVERY__: boolean | undefined;
declare const __SOLOMON_YAAS_API_URL__: string | undefined;

type EffectiveEnvironments = Exclude<Environment, "Test">;

type SolanaNetwork = "mainnet-beta" | "devnet" | "testnet";

interface ApiUrls {
    solomonData: Url;
    yaas: Url;
    yaasMetrics: Url;
    rewards: Url;
}

/**
 * Feature flags — boolean toggles for surfaces in development. Resolved
 * per environment so we can ship dark to Production while iterating in
 * Development/Staging.
 *
 * The environment is picked from `window.location.hostname` (see
 * `environment` below), so anything not on a known Staging or Production
 * host — localhost included — reads the **Development** column of
 * `config.json`. A flag that is off in Production may still be on there,
 * which makes the code it gates dev-only rather than dead. `config.json`
 * supplies defaults. Main app builds can override advanced routing with
 * `FEATURE_ADVANCED_ROUTING` and Venues with `FEATURE_DISCOVERY` (exactly
 * "true" or "false"). These public build-time values require a rebuild or
 * dev-server restart. The notes below say what each flag gates.
 *
 * - `yaasNav`: gates the "YaaS" item in the main app's navigation. Flip
 *   Production to `true` once `/yaas` is ready to ship.
 * - `portfolio`: gates the "Portfolio" nav item in the yaas-client console.
 *   Hidden for now; flip on once it's ready.
 * - `clientOperatingContextSelection`: gates the multi-issuer selector in
 *   the yaas-client console. Defaults on to preserve current compatibility
 *   while backend strict context mode is still opt-in.
 * - `complianceModuleUi`: gates issuer-side compliance-module assignment
 *   and review affordances. Keep this on while the module/review backend is
 *   active.
 * - `clientComplianceSurface`: gates the yaas-client Compliance page,
 *   Compliance sidebar item, and the client compliance redirect/write-block
 *   experience. Defaults off while the client console continues to rely on
 *   onboarding as the visible evidence workflow.
 * - `retailPositionRouting`: master gate for the retail per-position reward
 *   routing editors on `/yaas`. **On in Development, off in Staging and
 *   Production** — their yaas-api endpoints were removed (`cb11ca4a`) and
 *   routing data returns via Jachin later, so the editors can only be
 *   exercised locally against mocks. The surfaces behind it are dev-only,
 *   not dead: do not delete them on the strength of the shipped
 *   environments. With this on and `retailMultiDestinationRouting` off,
 *   `/yaas` ships single-destination routing only.
 * - `retailMultiDestinationRouting`: layers multi-destination (percentage
 *   split) routing on top of `retailPositionRouting`. Gates the "Switch to
 *   multiple destinations routing" footer link, the Saved Destinations
 *   entry-point, the Reward Destinations panel, and the split-routing
 *   surface. **Defaults off in all environments for retail launch.** While it
 *   is off `advancedMode` can never be entered and `/yaas` stays on the
 *   single-destination inline editor; it has no effect at all while
 *   `retailPositionRouting` is off.
 * - `retailEnrollmentDestination`: shows the Rewards Destination group
 *   (nickname + address) and its Squads wallet check on the retail Enable
 *   Rewards form. **Off in all environments for retail launch**: the
 *   form says rewards go to the wallet you enroll with, enrollment always
 *   submits the connected wallet as the destination, and the Squads
 *   preflight neither runs nor gates the submit, since a connected wallet
 *   cannot be a Squads vault. Turn it on in Development to exercise the
 *   field locally. Independent of `retailPositionRouting`, which gates the
 *   post-enrollment editors.
 * - `retailDistributionModeChoice`: offers the Stream / Claim choice on
 *   retail `/yaas`: the "Distribution (Select One)" toggle on the Enable
 *   Rewards form and the "Switch to stream / claim rewards" footer link.
 *   **Off in all environments for retail launch**: enrollment always submits
 *   Stream, and an enrolled account cannot switch modes from `/yaas`.
 * - `venues`: gates the `/venues` coverage registry — the route
 *   registration, the "Venues" nav item, and the "Additional Earnings in
 *   Venues" ghost row at the bottom of the `/yaas` positions breakdown.
 *   **Defaults off in all environments for retail launch**, until the venue
 *   set and its rates are real (rates are still mock; yaas-api has no
 *   public read of the Surface catalog yet).
 *
 * Note: the yaas-client lite-onboarding switch deliberately does NOT live
 * here. It is server-owned (`onboarding.liteEnabled` in yaas-api) and read
 * back as `liteOnboardingEnabled` on the client application-bundle
 * responses, so the UI can never disagree with the submit-lite endpoint.
 */
interface FeatureFlags {
    /**
     * A mock reserve-history chart on the /stats Reserves tab.
     *
     * The data behind it does not exist: the protocol feed returns only a
     * current snapshot, and no endpoint carries per-asset balances over time.
     * The chart is drawn from synthesized figures so the shape of the
     * disclosure can be argued about before anyone builds the pipeline, and it
     * says so on its face.
     *
     * Development only, and it must stay that way until a real series backs
     * it. A reserves page is read as a claim, and a plausible invented line on
     * one is worse than no line at all.
     */
    reserveHistoryPreview: boolean;
    yaasNav: boolean;
    portfolio: boolean;
    clientOperatingContextSelection: boolean;
    complianceModuleUi: boolean;
    clientComplianceSurface: boolean;
    /**
     * Multi-destination (split) rewards routing on the Company Recipient
     * Console's position page: the +Add Route row under the Rewards Routing
     * table in edit mode. The client console's own sibling of
     * `retailMultiDestinationRouting`, which is the retail `/yaas` flag and
     * off everywhere. On in Development only: yaas-api saves one destination
     * per position today.
     */
    clientMultiDestinationRouting: boolean;
    /**
     * Whether external clients can enter the console beyond `/onboarding`.
     *
     * Off (Staging and Production today) hides the rest of the app: an external
     * applicant stays on the onboarding surface whatever their application
     * status is, approval included. The post-approval celebration still plays
     * once and still acknowledges, it just holds instead of redirecting.
     * Active, eligible issuer self-clients are exempt from this release hold.
     * On in Development; onboarding requirements still apply.
     */
    clientConsoleAccess: boolean;
    /**
     * Which `/onboarding` flow renders. Off (everywhere today) pins the route
     * to the trimmed single-step `OnboardingLite` — the shippable path. On
     * restores the server-driven choice, where yaas-api's
     * `liteOnboardingEnabled` picks lite vs the full multi-step `OnboardingHub`.
     *
     * Full Onboarding is not deleted, only switched off: it is the target
     * design and comes back when document upload, vendor signing, and deeper
     * proof collection are ready.
     */
    clientFullOnboarding: boolean;
    /** Paid wallet-verification fallback; enable per environment after hardware-wallet testing. */
    retailMicroTransferVerification: boolean;
    /** Main-app Squads destination enrollment; enabled per environment. */
    retailSquadsMultisig: boolean;
    retailPositionRouting: boolean;
    /** Temporary direct supported Raydium LP NFT discovery while backend discovery is being reconciled. */
    retailRaydiumLpFallback: boolean;
    /** Opt in to YaaS/Jachin LP discovery and principal; false preserves the RPC provider. */
    retailLpApiDiscovery: boolean;
    retailMultiDestinationRouting: boolean;
    retailEnrollmentDestination: boolean;
    retailDistributionModeChoice: boolean;
    venues: boolean;
}

interface AppEnvironmentConfig {
    rpcEndpoint: Url;
    network: SolanaNetwork;
    apiUrls: ApiUrls;
    verificationWallet: string;
    featureFlags: FeatureFlags;
}

interface EnvironmentConfig<Env extends EffectiveEnvironments = EffectiveEnvironments> {
    config: AppEnvironmentConfig;
    environment: Env;
}

interface AppConfig {
    name: string;
    shortName: string;
    version: string;
    environments: Record<EffectiveEnvironments, EnvironmentConfig>;
}

interface YaasOAuthConfig {
    apiBase: string;
    clientId: string;
    redirectUri: string;
    scope: string;
    externalAuth?: {
        google?: {
            enabled: boolean;
            label?: string;
        };
        sso?: {
            enabled: boolean;
            label?: string;
        };
    };
}

interface YaasAppEnvironmentConfig {
    oauth: YaasOAuthConfig;
}

interface YaasEnvironmentConfig<Env extends EffectiveEnvironments = EffectiveEnvironments> {
    config: YaasAppEnvironmentConfig;
    environment: Env;
}

interface YaasAppConfig {
    name: string;
    shortName: string;
    version: string;
    environments: Record<EffectiveEnvironments, YaasEnvironmentConfig>;
}

/**
 * A YaaS console that submits chain transactions owns its RPC and network
 * settings. This prevents it from inheriting the main app's mainnet values.
 */
interface ChainEnabledYaasAppEnvironmentConfig extends YaasAppEnvironmentConfig {
    rpcEndpoint: Url;
    network: SolanaNetwork;
}

interface ChainEnabledYaasEnvironmentConfig<Env extends EffectiveEnvironments = EffectiveEnvironments> {
    config: ChainEnabledYaasAppEnvironmentConfig;
    environment: Env;
}

interface ChainEnabledYaasAppConfig {
    name: string;
    shortName: string;
    version: string;
    environments: Record<EffectiveEnvironments, ChainEnabledYaasEnvironmentConfig>;
}

type App = "main" | "yaas-client" | "yaas-issuer" | "yaas-issuer-legacy" | "wu-poc";

interface RawConfig {
    appHint: App;
    apps: {
        main: AppConfig;
        "yaas-client": YaasAppConfig;
        "yaas-issuer": ChainEnabledYaasAppConfig;
        "yaas-issuer-legacy": YaasAppConfig;
        "wu-poc": ChainEnabledYaasAppConfig;
    };
}

// This widens but works well enough, since it will complain otherwise
type _TypeChecked = AssertLooselyAssignable<RawConfig, typeof rawConfig>;

const hasWindow = typeof window !== "undefined";
const hostname = hasWindow ? window.location.hostname : "";
const browserOrigin = hasWindow ? window.location.origin : null;
const localYaasApiUrl =
    ["localhost", "127.0.0.1", "[::1]"].includes(hostname) && typeof __SOLOMON_YAAS_API_URL__ !== "undefined"
        ? __SOLOMON_YAAS_API_URL__
        : "";

const app: App = "main";
function getBrowserCallbackUri(): string | null {
    return browserOrigin ? `${browserOrigin}/callback` : null;
}

function resolveBrowserOAuthConfig(config: YaasOAuthConfig): YaasOAuthConfig {
    const redirectUri = getBrowserCallbackUri();
    return {
        ...config,
        apiBase: localYaasApiUrl || config.apiBase,
        ...(redirectUri ? { redirectUri } : {})
    };
}

const environment = resolveAppEnvironment(hostname);

export const APP_ENVIRONMENT = environment;

export const { name: APP_NAME, shortName: APP_SHORT_NAME, version: APP_VERSION } = rawConfig.apps[app];
const baseAppConfig = rawConfig.apps[app].environments[environment];
export const APP_CONFIG = {
    ...baseAppConfig,
    config: {
        ...baseAppConfig.config,
        ...networkConnection(baseAppConfig.config.rpcEndpoint),
        apiUrls: {
            ...baseAppConfig.config.apiUrls,
            yaas: localYaasApiUrl || baseAppConfig.config.apiUrls.yaas
        }
    }
} as EnvironmentConfig;

// Solana connection config
export const RPC_ENDPOINT = APP_CONFIG.config.rpcEndpoint as Url;
export const SOLANA_NETWORK = APP_CONFIG.config.network as SolanaNetwork;

// Verification wallet address (for wallet ownership proof)
export const VERIFICATION_WALLET_ADDRESS = APP_CONFIG.config.verificationWallet;

// API URLs (environment-aware)
export const API_URLS = {
    SOLOMON_DATA: APP_CONFIG.config.apiUrls.solomonData as Url,
    YAAS: APP_CONFIG.config.apiUrls.yaas as Url,
    YAAS_METRICS: APP_CONFIG.config.apiUrls.yaasMetrics as Url,
    REWARDS: APP_CONFIG.config.apiUrls.rewards as Url
} as const;

// Explicit build-time overrides take precedence over hostname-selected defaults.
export const FEATURE_FLAGS: FeatureFlags = {
    ...APP_CONFIG.config.featureFlags,
    retailLpApiDiscovery:
        (typeof __FEATURE_RETAIL_LP_API_DISCOVERY__ === "undefined"
            ? undefined
            : __FEATURE_RETAIL_LP_API_DISCOVERY__) ?? APP_CONFIG.config.featureFlags.retailLpApiDiscovery,
    retailMultiDestinationRouting:
        (typeof __FEATURE_ADVANCED_ROUTING__ === "undefined" ? undefined : __FEATURE_ADVANCED_ROUTING__) ??
        APP_CONFIG.config.featureFlags.retailMultiDestinationRouting,
    venues:
        (typeof __FEATURE_DISCOVERY__ === "undefined" ? undefined : __FEATURE_DISCOVERY__) ??
        APP_CONFIG.config.featureFlags.venues
};

// YaaS app OAuth configs (resolved by environment)
export const YAAS_CLIENT_OAUTH = resolveBrowserOAuthConfig(
    rawConfig.apps["yaas-client"].environments[environment].config.oauth
);
export const YAAS_ISSUER_OAUTH = resolveBrowserOAuthConfig(
    rawConfig.apps["yaas-issuer"].environments[environment].config.oauth
);
export const YAAS_ISSUER_LEGACY_OAUTH = resolveBrowserOAuthConfig(
    rawConfig.apps["yaas-issuer-legacy"].environments[environment].config.oauth
);
export const WU_POC_OAUTH = resolveBrowserOAuthConfig(rawConfig.apps["wu-poc"].environments[environment].config.oauth);

// Issuer console Solana connection. It is separate from the main app values,
// so the copied console cannot silently submit its dev flow to mainnet.
const issuerNetwork = networkConnection(rawConfig.apps["yaas-issuer"].environments[environment].config.rpcEndpoint);
export const YAAS_ISSUER_RPC_ENDPOINT = issuerNetwork.rpcEndpoint as Url;
export const YAAS_ISSUER_SOLANA_NETWORK = issuerNetwork.network as SolanaNetwork;

// wu-poc Solana connection. It remains separate so wu-poc keeps its own
// deployment settings after the issuer console starts to diverge.
//
// The endpoint is a Helius *secure URL* — the credential is the subdomain, so
// there is no `?api-key=` to leak when this file is bundled into the browser.
// Same shape as the mainnet endpoint above. Secure URLs are capped at 5 req/s
// per IP, which is ample for a demo console but would throttle any polling.
export const WU_POC_RPC_ENDPOINT = rawConfig.apps["wu-poc"].environments[environment].config.rpcEndpoint as Url;
export const WU_POC_SOLANA_NETWORK = rawConfig.apps["wu-poc"].environments[environment].config.network as SolanaNetwork;

export { solomonTailwindExtendTheme, solomonTailwindFontStacks, solomonTailwindThemeTokens } from "./tailwind/theme";

// All environments use current reporting through their configured Solomon Data API.
export const JACHIN_REPORTING_ENABLED = true;
export const JACHIN_REPORTING_API_URL = API_URLS.SOLOMON_DATA;
